// Account operations use v2 directly, retaining the response shapes consumed by existing screens.
import type { DataExportAsk } from './accounts-v2.ts'
import { requestV2 } from './v2-only.ts'

const sides = () => import('./accounts-v2.ts')

/** The account document, including the persona and chat allowance used by AuthContext. */
export const getUserInfo = (token: string, signal?: AbortSignal) =>
  requestV2(sides, (api) => api.userInfo(token, { signal }))

/** The plan in force is read from the server; the retired device argument remains for existing callers. */
export const checkSubscriptionStatus = (token: string, _deviceId: string) =>
  requestV2(sides, (api) => api.subscription(token, {}))

/** Settings' current username and bio. */
export const getSettings = (token: string) =>
  requestV2(sides, (api) => api.settings(token, {}))

/** Toggle safe mode using the current server value and country restrictions. */
export const toggleSafeMode = (token: string, _uid: string) =>
  requestV2(sides, (api) => api.flipSafeMode(token, {}))

export const updateUsername = (token: string, username: string) =>
  requestV2(sides, (api) => api.changeMe(token, {}, { username }))

export const updateBio = (token: string, bio: string) =>
  requestV2(sides, (api) => api.changeMe(token, {}, { bio }))

export const deleteUser = (token: string) =>
  requestV2(sides, (api) => api.requestDeletion(token, {}))

export const restoreUser = (token: string) =>
  requestV2(sides, (api) => api.cancelDeletion(token, {}))

/** Issue a new outside-API key once; the server revokes its predecessor atomically. */
export const createApiToken = (token: string) =>
  requestV2(sides, (api) => api.apiKey(token, {}))

/** The server accepts the owned media id; the old URL argument remains for existing callers. */
export const setProfilePicture = (
  token: string,
  _imageUrl: string,
  imageId: string,
) =>
  requestV2(sides, (api) =>
    api.changeMe(token, {}, { profile_picture: imageId }),
  )

export const getProfile = (token: string) =>
  requestV2(sides, (api) => api.profileImage(token, {}))

export const startAgeCheck = (token: string) =>
  requestV2(sides, (api) => api.ageCheck(token, {}))

/** Staff authority and the reason are checked by the server; keepalive survives page closure. */
export const banUser = (
  token: string,
  userId: string,
  reason: string,
  { keepalive = false }: { keepalive?: boolean } = {},
) => requestV2(sides, (api) => api.ban(token, { keepalive }, userId, reason))

export const unbanUser = (
  token: string,
  userId: string,
  { keepalive = false }: { keepalive?: boolean } = {},
) => requestV2(sides, (api) => api.liftBan(token, { keepalive }, userId))

/** "Download my data": the person's exports, newest first, with each one's state and parts (v2's shape). */
export const getDataExports = (token: string, signal?: AbortSignal) =>
  requestV2(sides, (api) => api.dataExports(token, { signal }))

/**
 * Asks for a copy of the person's data: everything (the default), since the last download, or chosen dates. The
 * server decides whether it may start now and says when if not, and checks the dates.
 */
export const startDataExport = (token: string, ask?: DataExportAsk) =>
  requestV2(sides, (api) => api.askDataExport(token, {}, ask))

/** A fresh download link for one part of a ready export; each one counts as one of the part's downloads. */
export const getDataExportLink = (
  token: string,
  exportId: string,
  part: number,
) => requestV2(sides, (api) => api.dataExportLink(token, {}, exportId, part))

/** The existing staff-only daily top-up, under the adapter's receipt number. */
export const claimSocialGems = (token: string) =>
  requestV2(sides, (api) => api.socialTopUp(token, {}))

/** "I agree to the content rules" on the moderation ladder's hold screen; v2 decides whether the hold lifts. */
export const agreeToContentRules = (token: string) =>
  requestV2(sides, (api) => api.agreeToRules(token, {}))
